import { useEffect, useRef, useState } from 'react'
import { SlidersHorizontal, X } from 'lucide-react'

type ThemeMode = 'system' | 'light' | 'dark'
type Density = 'normal' | 'compact'

const STORAGE_KEY = 'me_tweaks_v1'

type Tweaks = { theme: ThemeMode; density: Density }

function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw)
  } catch {
    // ignore — use defaults
  }
  return { theme: 'system', density: 'normal' }
}

function applyTheme(mode: ThemeMode) {
  const root = document.documentElement
  if (mode === 'system') {
    root.style.colorScheme = ''
    root.removeAttribute('data-theme')
  } else {
    root.style.colorScheme = mode
    root.setAttribute('data-theme', mode)
  }
}

function applyDensity(density: Density) {
  document.documentElement.style.setProperty(
    '--me-density',
    density === 'compact' ? '0.85' : '1',
  )
  document.documentElement.setAttribute('data-density', density)
}


// Module-level effect runner: applies persisted tweaks once at app boot
// so theme/density are correct even before TweaksPanel mounts.
let _bootedTweaks = false
function bootTweaksOnce() {
  if (_bootedTweaks) return
  _bootedTweaks = true
  const t = loadTweaks()
  applyTheme(t.theme)
  applyDensity(t.density)
}


/**
 * TweaksPanel renders inline (intended to live in the top bar). It's
 * a compact gear button that pops a card below itself with theme +
 * density toggles. State is persisted to localStorage and applied to
 * the document root.
 */
export default function TweaksPanel() {
  const [open, setOpen] = useState(false)
  const [tweaks, setTweaks] = useState<Tweaks>(() => {
    bootTweaksOnce()
    return loadTweaks()
  })
  const wrapRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    applyTheme(tweaks.theme)
    applyDensity(tweaks.density)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(tweaks))
  }, [tweaks])

  // Close on outside click + Escape.
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={wrapRef} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="me-btn me-btn-outline"
        title="Appearance tweaks (theme, density)"
      >
        <SlidersHorizontal size={13} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-1 w-72 me-card p-4"
          style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        >
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[13px] font-semibold me-text">Tweaks</div>
            <button onClick={() => setOpen(false)} className="me-btn"><X size={12} /></button>
          </div>

          <div className="me-section-h">Theme</div>
          <div className="mb-3 grid grid-cols-3 gap-1">
            {(['system', 'light', 'dark'] as ThemeMode[]).map((m) => (
              <button
                key={m}
                onClick={() => setTweaks({ ...tweaks, theme: m })}
                data-selected={tweaks.theme === m ? 'true' : undefined}
                className="me-tab justify-center"
              >
                {m}
              </button>
            ))}
          </div>

          <div className="me-section-h">Density</div>
          <div className="grid grid-cols-2 gap-1">
            {(['normal', 'compact'] as Density[]).map((d) => (
              <button
                key={d}
                onClick={() => setTweaks({ ...tweaks, density: d })}
                data-selected={tweaks.density === d ? 'true' : undefined}
                className="me-tab justify-center"
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
